import React from 'react';
import ReactDOM from 'react-dom';
import Director from 'director';
import { Provider } from 'react-redux';

import * as API from 'es6/core/api';
import AggregateMemory from 'es6/models/aggregate-memory';
import Goal from 'es6/models/goal';
import Grouping from 'es6/models/grouping';
import IKnowCumulatives from 'es6/models/iknow-cumulatives';
import Logger from 'es6/services/logger';
import RecommendedNext from 'es6/components/home/recommended-next';
import SiteController from 'es6/controllers/site-controller';
import { StudyDetailsPanels } from 'es6/components/home/study-details';
import StudyDetails from 'es6/containers/home/study-details';
import StudyProgress from 'es6/components/home/study-progress';
import StudyTime from 'es6/components/home/study-time';
import { UserSettings } from 'es6/models/user-settings';

export default class HomeController extends SiteController {
  constructor(store, panel = null) {
    super(store);

    // Instance variables.
    this.aggregateMemories = {};
    this.backgroundRequest = null;
    this.customGoals = [];
    this.customGoalsResponse = null;
    this.goals = [];
    this.groupings = [];
    this.panel = panel;
    this._leCumulatives = null;
    this._iKnowCumulatives = null;
    this.courseDirectoryContainer = document.querySelector('.course-directory');
    this.recommendedContainer = document.querySelector('.recommended-container');
    this.refreshIntervalId = null;
    this.studyAllLanguage = null;
    this.studyDetailsContainer = document.querySelector('.study-details-container');
    this.studyProgressContainer = document.querySelector('.study-progress-container');
    this.studyTimeContainer = document.querySelector('.study-time-container');
    this.router = null;

    // Set up routes for the router. They are initiallized after components mount.
    const routes = {};
    _.values(StudyDetailsPanels).forEach((route) => {
      routes[`/${route}`] = this.update.bind(this, route);
    });

    // This router uses hashes, instead of html5 push state.
    this.router = new Director.Router(routes).configure({ html5history: false });

    // Default to now studying if nothing is there.
    this.router.configure({
      notfound: () => this.update(StudyDetailsPanels.FOCUSED),
    });

    // Render placeholders.
    this.renderCumulativeDataComponents(null);

    // Get users goals and render. Afterwards, initiate the appropriate URL.
    this.fetchData().then(() => this.router.init(window.location.hash.substr(1)));
  }

  /**
   * Retrieves the data necessary to render the components.
   */
  fetchData() {
    // Call the aggregate memory API. The custom goal API is called in the constructor because
    // it only needs to be called once.
    const aggregateMemoryResponse = API.getEnrolledAggregateMemories({ includeGoals: true });

    // This only needs to be called once.
    if (!this.customGoalsResponse) {
      this.customGoalsResponse = API.getUserCreatedGoals();
    }

    // Grab user settings for study all language.
    const userSettingsResponse = API.getSettings();

    // Grab the cumulative data and load the cumulative data components.
    // Since the routing and course details are independent of cumulative data,
    // just render the cumulative data components as soon as it loads.
    API.getLearningEngineCumulatives().then(leCumulatives => {
      this._leCumulatives = leCumulatives;
      this.renderCumulativeDataComponents();
    }).catch(this.handleAPIError);

    API.getIKnowCumulatives().then(iKnowCumulatives => {
      this._iKnowCumulatives = new IKnowCumulatives(iKnowCumulatives);
      this.renderCumulativeDataComponents();
    }).catch(this.handleAPIError);

    // Once all the data for courses is loaded, render those components.
    return Promise.all([aggregateMemoryResponse, this.customGoalsResponse,
      userSettingsResponse]).then((responses) => {
        // Reset data.
        this.aggregateMemories = {};
        this.goals = [];
        this.groupings = [];

        const memories = responses[0];
        this.customGoals = responses[1].map(g => new Goal(g));
        const userSettings = new UserSettings(responses[2]);

        memories.goals.forEach((memory) => {
          this.aggregateMemories[memory.goalId] = new AggregateMemory(memory);
        });

        memories.goalData.forEach((goal) => {
          this.goals.push(new Goal(goal));
        });

        this.groupings = memories.groupings.map(g => new Grouping(g));

        // Set the study all language.
        this.studyAllLanguage = userSettings.global.studyAllLanguage;

        // Render components
        this.renderCourseComponents();

        // Rerender the components every minute.
        clearInterval(this.refreshIntervalId);
        this.refreshIntervalId = setInterval(this.renderCourseComponents.bind(this), 60000);
      }).catch(this.handleAPIError);
  }

  /**
   * Get the goal with the further study time from now.
   *
   * Order by incomplete and seeNextAt
   */
  getRecommendedGoal() {
    const goals = this.goals.filter((g) => {
      const aggregate = this.aggregateMemories[g.id];
      return aggregate.focused;
    }).sort((gA, gB) => {
      const aggregateA = this.aggregateMemories[gA.id];
      const aggregateB = this.aggregateMemories[gB.id];

      return aggregateA.recommendationOrder > aggregateB.recommendationOrder ? 1 : -1;
    });

    return goals.length > 0 ? goals[0] : null;
  }

  getRecommendedNextProps() {
    const goal = this.getRecommendedGoal();
    let lang = this.studyAllLanguage;

    if (!goal) {
      return null;
    }

    // If the grouping does not contain the language in the user settings, just choose
    // the first language.
    if (!Grouping.groupingsContainLanguage(this.groupings, lang)) {
      lang = this.groupings[0].grouping.cueLanguageCode;
    }

    return {
      aggregateMemory: this.aggregateMemories[goal.id],
      goal,
      groupings: this.groupings,
      language: lang,
      onLanguageChange: this.handleLanguageChange.bind(this),
    };
  }

  getStudyDetailsProps() {
    return {
      aggregateMemories: this.aggregateMemories,
      customGoals: this.customGoals,
      goals: this.goals,
      panel: this.panel,
      onUnenroll: this.handleUnenroll.bind(this),
      onFocus: this.handleFocus.bind(this),
      onUnfocus: this.handleUnfocus.bind(this),
      onTabClick: this.handleTabClick.bind(this),
    };
  }

  handleAPIError(reason) {
    Logger.error('HomeController', reason, reason.stack);
  }

  /**
   * Handles the language changing of the recommended next field.
   */
  handleLanguageChange(language) {
    // Update the language and re-render.
    this.studyAllLanguage = language;

    // Cancel previous update request if necessary.
    if (this.backgroundRequest) {
      this.backgroundRequest.abort();
    }

    // Save in the background.
    this.backgroundRequest = API.postSettings({
      global: {
        studyAllLanguage: language,
      },
    });

    // Rerender to reflect language change.
    this.renderCourseComponents();
  }

  handleUnenroll(goal) {
    API.postUnenrollGoal(goal.id)
      .then(() => this.fetchData())
      .catch((reason) => {
        Logger.error('Home Controller', reason, reason.stack);
      });
  }

  handleFocus(goal) {
    API.postFocusGoal(goal.id)
      .then(() => this.fetchData())
      .catch((reason) => {
        Logger.error('Home Controller', reason, reason.stack);
      });
  }

  handleUnfocus(goal) {
    API.postUnfocusGoal(goal.id)
      .then(() => this.fetchData())
      .catch((reason) => {
        Logger.error('Home Controller', reason, reason.stack);
      });
  }

  handleTabClick(panel) {
    this.router.setRoute(`/${panel}`);
  }

  /**
   * Renders the study progress and total time studied components.
   */
  renderCumulativeDataComponents() {
    ReactDOM.render(
      <StudyProgress cumulatives={this._leCumulatives} />,
      this.studyProgressContainer
    );

    ReactDOM.render(
      <StudyTime iKnowCumulatives={this._iKnowCumulatives} />,
      this.studyTimeContainer
    );
  }


  /**
   * Renders the study panel and recommendation panel.
   */
  renderCourseComponents() {
    const studyProps = this.getStudyDetailsProps();
    const recommendedProps = this.getRecommendedNextProps();

    ReactDOM.render(
      <Provider store={this.store}>
        <StudyDetails {...studyProps} />
      </Provider>,
      this.studyDetailsContainer
    );

    // If the user doesn't have anything to study. Show the add courses menu.
    // Otherwise, render recommended next component.
    // 1) User has a recommended goal to study. Show recommended container.
    // 2) User has goals but nothing recommended. Show course directory and set
    //    class to 'has-courses'
    // 3) User does not have any goals (ex, new user). Show course directory as is.
    if (recommendedProps === null) {
      this.recommendedContainer.style.display = 'none';

      // Get rid of the classname so it doesn't pile up. When support is
      // dropped for IE9, can use classList.
      this.courseDirectoryContainer.className.replace(' has-courses', '');

      if (this.goals.length > 0) {
        this.courseDirectoryContainer.className += ' has-courses';
      }

      this.courseDirectoryContainer.style.display = 'block';
    } else {
      this.recommendedContainer.style.display = 'block';
      this.courseDirectoryContainer.style.display = 'none';

      ReactDOM.render(<RecommendedNext {...recommendedProps} />, this.recommendedContainer);
    }
  }

  remove() {
    if (this.refreshIntervalId) {
      clearInterval(this.refreshIntervalId);
    }

    // Turn off the router.
    this.router.off();

    ReactDOM.unmountComponentAtNode(this.recommendedContainer);
    ReactDOM.unmountComponentAtNode(this.studyDetailsContainer);
  }

  update(panel) {
    this.panel = panel;
    this.renderCourseComponents();
  }
}
